import React, { useState, useEffect, useCallback } from 'react';
import { Text, Flex, theme, Button, Link } from '@xometry/xometry_loft';
import { Cell } from 'react-table';
import Table from '../../../components/Table';
import { FullOrganizationInvitationFragment } from '../../../schema/generated/schema';
import { InviteModalData } from '..';
import OrganizationMemberHelper, {
  PAGINATION_PAGE_SIZE,
} from '../../Organization/TeamSettings/TeamMemberManagement/helper';

interface PendingInvitesProps {
  isStaff: boolean;
  invitationModalData: InviteModalData;
  setInvitationModalData: (arg0: any) => void;
  pendingInvites: FullOrganizationInvitationFragment[] | [];
  loadingInvites: boolean;
}

interface InvitationData {
  organization: {
    name: string;
    activeMembers: number;
    id: string;
  };
  senderUserData: {
    firstName: string;
    lastName: string;
    email: string;
  };
  userData: {
    email: string;
  };
  declineInviteUrl: string;
  acceptInviteUrl: string;
}

const PendingInvitesTab = ({
  isStaff,
  invitationModalData,
  setInvitationModalData,
  pendingInvites,
  loadingInvites,
}: PendingInvitesProps) => {
  const [maxPages, setMaxPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [currentMembers, setCurrentMembers] = useState<
    FullOrganizationInvitationFragment[]
  >([]);

  useEffect(() => {
    if (pendingInvites.length > 0) {
      setMaxPages(Math.ceil(pendingInvites.length / PAGINATION_PAGE_SIZE));
      setCurrentMembers(pendingInvites.slice(selectedPage - 1, 5));
    }
  }, [pendingInvites.length]);

  const PendingMemberColumns = () => {
    return [
      {
        Header: 'Team Name',
        accessor: 'organization.name',
        Cell: ({ cell }: { cell: Cell<InvitationData> }) => {
          const teamSettingsUrl = `/account/organization/${cell.row.original.organization.id}/settings`;
          return (
            <Text kind="label" padding="0px">
              {isStaff ? (
                <Link href={teamSettingsUrl}>{cell.value}</Link>
              ) : (
                cell.value
              )}
            </Text>
          );
        },
      },
      {
        Header: 'Members',
        accessor: 'organization.activeMembers',
        Cell: ({ cell }: { cell: Cell }) => (
          <Text kind="body-sm">
            {cell.value} Member{cell.value > 1 ? 's' : ''}
          </Text>
        ),
      },
      {
        Header: 'Invited By',
        accessor: 'senderUserData',
        Cell: ({ cell }: { cell: Cell<InvitationData> }) => (
          <Text kind="body-sm">
            {cell.value.email.endsWith('@xometry.com')
              ? 'Xometry Staff'
              : `${cell.value?.firstName} ${cell.value?.lastName}`}
          </Text>
        ),
      },
      {
        accessor: 'declineInviteUrl',
        Cell: ({ cell }: { cell: Cell<InvitationData> }) => (
          <Button
            kind="text"
            scale="small"
            onClick={() =>
              setInvitationModalData({
                ...invitationModalData,
                inviterFirstName: cell.row.original.senderUserData.firstName,
                inviterLastName: cell.row.original.senderUserData.lastName,
                teamName: cell.row.original.organization.name,
                openStatus: true,
                url: cell.value,
                buttonDecision: 'Decline',
              })
            }
          >
            Decline
          </Button>
        ),
      },
      {
        accessor: 'acceptInviteUrl',
        Cell: ({ cell }: { cell: Cell<InvitationData> }) => (
          <Button
            scale="small"
            kind="primary"
            onClick={() =>
              setInvitationModalData({
                ...invitationModalData,
                inviterFirstName: cell.row.original.senderUserData.firstName,
                inviterLastName: cell.row.original.senderUserData.lastName,
                teamName: cell.row.original.organization.name,
                openStatus: true,
                url: cell.value,
                buttonDecision: 'Accept',
                userEmail: cell.row.original.userData.email,
                orgId: cell.row.original.organization.id,
              })
            }
          >
            Accept
          </Button>
        ),
      },
    ];
  };

  // React callback to manage fetch calls
  const fetchMemberData = useCallback(
    (currentPage = 1, pageSize = 5) => {
      OrganizationMemberHelper.fetchMembersInMemory(
        pendingInvites,
        setCurrentMembers,
        currentPage,
        pageSize
      );
    },
    [selectedPage]
  );

  return (
    <>
      {pendingInvites.length > 0 && (
        <Table
          data={currentMembers}
          columns={PendingMemberColumns()}
          loading={loadingInvites}
          hideHeader={false}
          showSelectColumn={false}
          isReadOnly={true}
          recordPageCount={maxPages}
          pageSize={PAGINATION_PAGE_SIZE}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          fetchCallback={fetchMemberData}
        />
      )}

      {pendingInvites.length === 0 && !loadingInvites && (
        <Flex
          flexDirection="column"
          alignItems="center"
          paddingY={theme.space[10]}
        >
          <img
            width="221px"
            alt="Vector Add Users"
            src="https://assets.xometry.com/images/spot-add-user.svg"
          />
          <Text
            kind="panel-title"
            marginTop={theme.space[5]}
            display="block"
            fontWeight={theme.fontWeights.semibold}
          >
            You Don&apos;t Have Any Pending Team Invitations
          </Text>
          <Text
            kind="body-sm"
            marginTop={theme.space[2]}
            display="block"
            maxWidth="450px"
            textAlign="center"
          >
            Any invitations to a Team will appear here. You can accept a Team
            invitation here, or from the email used for your Xometry account.
          </Text>
        </Flex>
      )}
    </>
  );
};

export default PendingInvitesTab;
